import React from 'react';
import LOGO from '../../IMG/logo192.jpg'
import { 
  RocketLaunchIcon, 
  LightBulbIcon, 
  HeartIcon, 
  AcademicCapIcon, 
  FlagIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const HomeObjectTive = () => {
    const features = [
        { 
            name: 'Purpose', 
            nameThai: 'วัตถุประสงค์',
            description: 'เราจะสร้างคุณภาพชีวิตที่ดีให้ผู้ป่วยโรคไต ที่รักษาด้วยการฟอกเลือดด้วยเครื่องไตเทียม',
            icon: HeartIcon,
            color: 'text-rose-600',
            bgColor: 'bg-rose-50',
            borderColor: 'border-rose-200',
            hoverBg: 'hover:bg-rose-50'
        },
        {
            name: 'Mission',
            nameThai: 'พันธกิจ',
            icon: RocketLaunchIcon,
            color: 'text-blue-600',
            bgColor: 'bg-blue-50',
            borderColor: 'border-blue-200',
            hoverBg: 'hover:bg-blue-50',
            description: [
                { value: 'Medical Service', value2: 'เพิ่มมาตรฐานการรักษาที่มีคุณภาพ' },
                { value: 'Customer Service', value2: 'สร้างความประทับใจ ให้บริการฟอกเลือดด้วยเครื่องไตเทียมอย่างมีคุณภาพ' },
                { value: 'Health Service', value2: 'ดูแลผู้ป่วยแบบองค์รวม เพื่อคุณภาพชีวิตที่ดี' },
            ]
        },
        {
            name: 'Core Value',
            nameThai: 'We Before Me',
            icon: LightBulbIcon,
            color: 'text-orange-600',
            bgColor: 'bg-orange-50',
            borderColor: 'border-orange-200',
            hoverBg: 'hover:bg-orange-50',
            description: [
                { value: 'We Can', emoji: '💪' },
                { value: 'We Care', emoji: '❤️' },
                { value: 'We Love', emoji: '🤝' },
                { value: 'We Happy', emoji: '😊' }
            ]
        },
        { 
            name: 'Core Competency',
            nameThai: 'ความเชี่ยวชาญหลัก', 
            description: 'เชี่ยวชาญด้านการดูแลรักษาผู้ป่วยฟอกเลือดด้วยเครื่องไตเทียม ดูแลดุจญาติมิตร',
            icon: AcademicCapIcon,
            color: 'text-indigo-600',
            bgColor: 'bg-indigo-50',
            borderColor: 'border-indigo-200',
            hoverBg: 'hover:bg-indigo-50'
        },
        { 
            name: 'Goal',
            nameThai: 'เป้าหมายหลัก', 
            description: 'เป็นผู้นำการบริการทางด้านไตเทียมอย่างครบวงจร',
            icon: FlagIcon,
            color: 'text-emerald-600',
            bgColor: 'bg-emerald-50',
            borderColor: 'border-emerald-200',
            hoverBg: 'hover:bg-emerald-50'
        },
    ];

    return (
        <section id='objectives' className="relative py-20 lg:py-28 bg-gradient-to-br from-slate-50 via-white to-blue-50 overflow-hidden">
            {/* Decorative Background Elements */}
            <div className="absolute inset-0 opacity-30">
                <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-200 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>

            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="lg:grid lg:grid-cols-12 lg:gap-16 items-start">
                    
                    {/* ฝั่งซ้าย: ข้อความหัวข้อและการ์ด */}
                    <div className="lg:col-span-7">
                        {/* Header Section - Enhanced */}
                        <div className="mb-12 text-center lg:text-left">
                            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 text-xs font-black tracking-[0.2em] text-orange-600 uppercase bg-gradient-to-r from-orange-100 to-orange-50 rounded-full border border-orange-200 shadow-sm">
                                <SparklesIcon className="w-4 h-4" />
                                Strategic Vision
                            </div>
                            <h2 className="text-4xl lg:text-5xl font-black tracking-tight text-slate-900 mb-4">
                                <span className="block">Objectives</span>
                                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-blue-700 to-orange-600">
                                    and Goals
                                </span>
                            </h2>
                            <div className="w-24 h-1.5 bg-gradient-to-r from-blue-600 to-orange-600 rounded-full mb-6 mx-auto lg:mx-0"></div>
                            <p className="text-lg text-slate-600 max-w-2xl leading-relaxed mx-auto lg:mx-0">
                                ความมุ่งมั่นและทิศทางในการดำเนินงานของ <span className="font-bold text-blue-900">"ราชพฤกษ์ไตเทียม"</span> 
                                เพื่อยกระดับมาตรฐานการรักษาและคุณภาพชีวิตที่ดีที่สุดสำหรับผู้ป่วย
                            </p>
                        </div>

                        {/* Features Grid - Enhanced Cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            {features.map((feature, idx) => (
                                <div 
                                    key={feature.name} 
                                    className={`relative flex flex-col p-6 bg-white rounded-3xl shadow-lg border-2 ${feature.borderColor} ${feature.hoverBg} hover:shadow-2xl hover:shadow-blue-900/10 transition-all duration-500 group transform hover:-translate-y-1`}
                                >
                                    {/* Icon and Title */}
                                    <div className="flex items-start gap-4 mb-4">
                                        <div className={`flex-shrink-0 p-3 rounded-2xl ${feature.bgColor} ${feature.color} group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-md`}>
                                            <feature.icon className="w-7 h-7" strokeWidth={2} />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-black text-slate-900 text-lg mb-1">{feature.name}</h3>
                                            <p className="text-sm font-bold text-slate-500">{feature.nameThai}</p>
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1">
                                        {typeof feature.description === 'string' ? (
                                            <p className="text-sm leading-relaxed text-slate-600 italic pl-1 border-l-4 border-slate-200 group-hover:border-blue-400 transition-colors duration-300">
                                                "{feature.description}"
                                            </p>
                                        ) : (
                                            <div className="space-y-2.5">
                                                {feature.description?.map((v, i) => (
                                                    <div 
                                                        key={i} 
                                                        className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100 group-hover:bg-white group-hover:border-slate-200 group-hover:shadow-md transition-all duration-300"
                                                    >
                                                        {v.emoji && <span className="text-xl flex-shrink-0">{v.emoji}</span>}
                                                        <div className="flex-1">
                                                            <p className="text-xs font-black text-blue-700 uppercase tracking-tight mb-1">{v.value}</p>
                                                            {v.value2 && <p className="text-sm text-slate-600 leading-snug">{v.value2}</p>}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Decorative Corner */}
                                    <div className={`absolute top-0 right-0 w-20 h-20 ${feature.bgColor} rounded-bl-full opacity-20 -z-10`}></div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ฝั่งขวา: รูปภาพและโลโก้ - Enhanced */}
                    <div className="mt-16 lg:mt-0 lg:col-span-5 lg:sticky lg:top-28">
                        <div className="relative group">
                            {/* Glowing Background Effect */}
                            <div className="absolute -inset-6 bg-gradient-to-tr from-orange-400 via-blue-500 to-blue-600 rounded-[3rem] opacity-20 blur-3xl group-hover:opacity-30 transition-opacity duration-700 animate-pulse"></div>
                            
                            {/* Main Image Container */}
                            <div className="relative aspect-square overflow-hidden rounded-[2.5rem] bg-white shadow-2xl border-8 border-white">
                                <img
                                    alt="ราชพฤกษ์ไตเทียม - ศูนย์ฟอกเลือดมาตรฐาน HA"
                                    src={LOGO}
                                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-1000"
                                    loading="lazy"
                                />
                                
                                {/* Overlay Gradient */}
                                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                
                                {/* Core Value Badge */}
                                <div className="absolute bottom-8 left-8 right-8 p-6 bg-white/95 backdrop-blur-xl rounded-[2rem] shadow-2xl border-2 border-blue-100 text-center transform group-hover:scale-105 transition-transform duration-500">
                                    <div className="flex items-center justify-center gap-2 mb-2">
                                        <HeartIcon className="w-6 h-6 text-orange-600 animate-pulse" />
                                        <p className="text-orange-600 font-black text-2xl italic">"We Before Me"</p>
                                    </div>
                                    <p className="text-slate-600 text-xs font-bold uppercase tracking-[0.2em]">Our Core Value</p>
                                    <div className="mt-3 flex justify-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-blue-600 animate-pulse"></div>
                                        <div className="w-2 h-2 rounded-full bg-orange-600 animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                                        <div className="w-2 h-2 rounded-full bg-emerald-600 animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                                    </div>
                                </div>
                            </div>

                            {/* Decorative Floating Elements */}
                            <div className="absolute -top-8 -right-8 w-32 h-32 bg-gradient-to-br from-orange-200 to-orange-300 rounded-full mix-blend-multiply filter blur-2xl opacity-70 animate-pulse"></div>
                            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-gradient-to-br from-blue-200 to-blue-300 rounded-full mix-blend-multiply filter blur-2xl opacity-70 animate-pulse" style={{ animationDelay: '1s' }}></div>
                            
                            {/* Stats Badge */}
                            <div className="absolute -top-6 -left-6 bg-gradient-to-br from-blue-600 to-blue-700 text-white px-6 py-4 rounded-2xl shadow-2xl shadow-blue-900/50 border-4 border-white transform -rotate-6 group-hover:rotate-0 transition-transform duration-500">
                                <p className="text-3xl font-black">20+</p>
                                <p className="text-xs font-bold uppercase tracking-wider">Years</p>
                            </div>
                        </div>

                        {/* Additional Info Cards */}
                        <div className="mt-8 grid grid-cols-2 gap-4">
                            <div className="bg-white p-5 rounded-2xl shadow-lg border-2 border-blue-100 hover:shadow-xl transition-all duration-300">
                                <p className="text-3xl font-black text-blue-600 mb-1">70+</p>
                                <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">ศูนย์ไตเทียม</p>
                            </div>
                            <div className="bg-white p-5 rounded-2xl shadow-lg border-2 border-orange-100 hover:shadow-xl transition-all duration-300">
                                <p className="text-3xl font-black text-orange-600 mb-1">5,000+</p>
                                <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">ผู้ป่วย</p>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* SEO Content (Hidden but crawlable) */}
            <div className="sr-only">
                <h2>วัตถุประสงค์และเป้าหมาย ราชพฤกษ์ไตเทียม</h2>
                <p>
                    ศูนย์ฟอกเลือดด้วยเครื่องไตเทียม ราชพฤกษ์ มุ่งมั่นสร้างคุณภาพชีวิตที่ดีให้ผู้ป่วยโรคไต 
                    ด้วยมาตรฐาน HA และทีมแพทย์พยาบาลผู้เชี่ยวชาญ บริการครบวงจรทั่วประเทศไทย
                </p>
            </div>
        </section>
    );
};

export default HomeObjectTive;
